import { AnimatePresence, motion } from 'framer-motion'
import React, { useRef } from 'react'
import { TopBarAction } from './TopBar'

interface TopBarAnimationWrapperProps {
  actions: TopBarAction[]
  animationType?: 'stagger' | 'fade' | 'slide' | 'none'
  animationProps?: {
    duration?: number
    staggerDelay?: number
    ease?: any
  }
}

interface AnimationPreset {
  initial: any
  animate: any
  exit: any
}

const ANIMATION_PRESETS: Record<string, AnimationPreset> = {
  stagger: {
    initial: { x: -32, opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: -32, opacity: 0 },
  },
  fade: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  },
  slide: {
    initial: { x: -20, opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: -20, opacity: 0 },
  },
  none: {
    initial: {},
    animate: {},
    exit: {},
  },
}

export function TopBarAnimationWrapper({
  actions,
  animationType = 'stagger',
  animationProps = {},
}: TopBarAnimationWrapperProps) {
  const { duration = 0.05, staggerDelay = 0.02, ease = [0.4, 0, 0.2, 1] } = animationProps
  const preset = ANIMATION_PRESETS[animationType] || ANIMATION_PRESETS.stagger
  const prevActionsRef = useRef<TopBarAction[]>([])
  const isInitialMount = useRef(true)
  const exitDelaysRef = useRef<Map<string, number>>(new Map())

  // Filter visible actions
  const visibleActions = actions.filter((action) => action.visible !== false)
  const prevActionIds = new Set(prevActionsRef.current.map((action) => action.id.toString()))
  const currentActionIds = new Set(visibleActions.map((action) => action.id.toString()))
  const isFirstRender = isInitialMount.current

  // Build position map of previous buttons for proper exit stagger calculation
  const prevPositionMap = new Map<string, number>()
  prevActionsRef.current.forEach((action, idx) => {
    prevPositionMap.set(action.id.toString(), idx)
  })

  // Check if we're removing buttons (need to delay entrances)
  const removingButtons = prevActionsRef.current.filter(
    (prevAction) => !currentActionIds.has(prevAction.id.toString())
  )
  const hasRemovals = removingButtons.length > 0 && !isFirstRender

  // Update refs after render
  React.useEffect(() => {
    prevActionsRef.current = [...visibleActions]
    if (isInitialMount.current) {
      isInitialMount.current = false
    }

    // Clean up exit delays for buttons that are now visible (not exiting anymore)
    const currentIds = new Set(visibleActions.map((a) => a.id.toString()))
    exitDelaysRef.current.forEach((_, id) => {
      if (currentIds.has(id)) {
        exitDelaysRef.current.delete(id)
      }
    })
  })

  if (animationType === 'none') {
    return (
      <>
        {visibleActions.map((action) => (
          <React.Fragment key={action.id}>{action.element}</React.Fragment>
        ))}
      </>
    )
  }

  // Store exit delays for removing buttons
  if (hasRemovals) {
    removingButtons.forEach((btn, idx) => {
      const exitDelay = (removingButtons.length - 1 - idx) * staggerDelay
      exitDelaysRef.current.set(btn.id.toString(), exitDelay)
    })
  }

  return (
    <AnimatePresence>
      {visibleActions.map((action, index) => {
        const actionId = action.id.toString()
        const isNew = isFirstRender || !prevActionIds.has(actionId)

        // For exit: use stored exit delay if it exists, otherwise 0
        const exitDelay = exitDelaysRef.current.get(actionId) ?? 0

        // For entrance: delay new buttons until ALL exits complete
        const totalExitTime = hasRemovals ? removingButtons.length * staggerDelay + duration : 0
        const enterDelay = isNew ? totalExitTime + index * staggerDelay : 0

        return (
          <motion.div
            key={action.id}
            className="relative"
            initial={
              isNew
                ? {
                    ...preset.initial,
                    x: -32 - index * 8,
                  }
                : false
            }
            animate={{
              ...preset.animate,
              transition: {
                duration,
                ease,
                delay: enterDelay,
              },
            }}
            exit={{
              ...preset.exit,
              transition: {
                duration,
                ease,
                delay: exitDelay,
              },
            }}
            style={{
              zIndex: animationType === 'stagger' ? 20 + index : 'auto',
            }}
          >
            {action.element}
          </motion.div>
        )
      })}
    </AnimatePresence>
  )
}

export default TopBarAnimationWrapper
